import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, User, LogIn, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Image from "../assets/Screenshot 2026-02-17 194019.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [vehiclesDropdownOpen, setVehiclesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const location = useLocation();
  const Y = "#FFD600", R = "#E5182E";

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Function to check if link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  const servicesDropdown = [
    { icon: "✈️", label: "Airport Transfer", desc: "On-time, every time", href: "/services/airport-transfer" },
    { icon: "📍", label: "Outstation Trips", desc: "Long distance comfort", href: "/services/outstation" },
    { icon: "🌙", label: "Local Packages", desc: "Verified drivers, 24/7", href: "/services/local-packages" },
  ];

  const vehicleDropdown = [
    { label: "SUV", desc: "Ertiga, Innova & Hycross", href: "/vehicles/suv" },
    { label: "Tempo Traveller", desc: "Group travel packages", href: "/vehicles/tempo-traveller" },
    { label: "Urbania", desc: "Premium group rides", href: "/vehicles/urbania" },
  ];

  // Handle mobile menu close
  const handleMobileLinkClick = () => {
    setIsOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <nav className="bg-black text-white sticky top-0 z-50 shadow-lg">
      <style jsx>{`
        @keyframes announcePulse {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-2" onClick={() => setIsOpen(false)}>
            <img src={Image} alt="PlusCabs Logo" className="w-16 h-16" />
          </Link>

          {/* Mobile Phone Number - Visible only on mobile */}
          <a 
            href="tel:+917349519354" 
            className="md:hidden flex items-center space-x-1 bg-yellow-400 text-black px-3 py-1.5 rounded-lg hover:bg-yellow-500 transition duration-300"
          >
            <Phone size={16} />
            <span className="text-sm font-semibold">+91-7349519354</span>
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`transition duration-300 font-medium ${
                isActive('/') 
                  ? 'text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-400'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className={`transition duration-300 font-medium ${
                isActive('/about') 
                  ? 'text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-400'
              }`}
            >
              About Us
            </Link>

            {/* Desktop Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <button 
                className={`flex items-center space-x-1 transition duration-300 font-medium ${
                  location.pathname.includes('/services') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
              >
                <span>Services</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Desktop Dropdown Menu */}
              {servicesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-black border border-gray-800 rounded-lg shadow-xl overflow-hidden">
                  {servicesDropdown.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      className="flex items-start space-x-3 px-4 py-3 hover:bg-gray-900 transition duration-200 border-b border-gray-800 last:border-b-0"
                      onClick={() => setServicesDropdownOpen(false)}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-white">{item.label}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/fleet" 
              className={`transition duration-300 font-medium ${
                isActive('/fleet') 
                  ? 'text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-400'
              }`}
            >
              Our Fleet
            </Link>

            {/* Desktop Vehicles Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setVehiclesDropdownOpen(true)}
              onMouseLeave={() => setVehiclesDropdownOpen(false)}
            >
              <button
                className={`flex items-center space-x-1 transition duration-300 font-medium ${
                  location.pathname.includes('/vehicles')
                    ? 'text-yellow-400'
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
              >
                <span>Vehicles</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${vehiclesDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {vehiclesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-black border border-gray-800 rounded-lg shadow-xl overflow-hidden">
                  {vehicleDropdown.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      className="block px-4 py-3 hover:bg-gray-900 transition duration-200 border-b border-gray-800 last:border-b-0"
                      onClick={() => setVehiclesDropdownOpen(false)}
                    >
                      <p className="font-semibold text-white">{item.label}</p>
                      <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/tour-packages" 
              className={`transition duration-300 font-medium ${
                isActive('/tour-packages') 
                  ? 'text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-400'
              }`}
            >
              Tour Packages
            </Link>

            <Link 
              to="/contact" 
              className={`transition duration-300 font-medium ${
                isActive('/contact') 
                  ? 'text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-400'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href="tel:+917349519354" 
              className="flex items-center space-x-2 bg-yellow-400 text-black px-4 py-2 rounded-lg hover:bg-yellow-500 transition duration-300 cursor-pointer"
            >
              <Phone size={18} />
              <span className="font-semibold">+91-7349519354</span>
            </a>
            <Link 
              to="/signin" 
              className="flex items-center space-x-2 bg-gray-800 px-4 py-2 rounded-lg hover:bg-gray-700 transition duration-300 cursor-pointer"
            >
              <User size={18} />
              <span className="font-semibold">Sign In</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen) setMobileServicesOpen(false); // Close services when opening menu
            }}
            className="md:hidden text-gray-300 hover:text-yellow-400 focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            <div className="flex flex-col space-y-4">
              {/* Mobile Navigation Links */}
              <Link 
                to="/" 
                className={`transition duration-300 py-2 px-2 ${
                  isActive('/') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={handleMobileLinkClick}
              >
                Home
              </Link>
              
              <Link 
                to="/about" 
                className={`transition duration-300 py-2 px-2 ${
                  isActive('/about') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={handleMobileLinkClick}
              >
                About Us
              </Link>
              
              {/* Mobile Services Section - Fixed Dropdown */}
              <div className="py-2">
                <button 
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className={`flex items-center justify-between w-full px-2 transition duration-300 font-medium ${
                    location.pathname.includes('/services') 
                      ? 'text-yellow-400' 
                      : 'text-gray-300'
                  }`}
                >
                  <span>Services</span>
                  <ChevronDown size={16} className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mobile Dropdown Menu */}
                {mobileServicesOpen && (
                  <div className="mt-2 ml-4 border-l-2 border-gray-800 pl-4">
                    {servicesDropdown.map((item, index) => (
                      <Link
                        key={index}
                        to={item.href}
                        className="block py-3 text-gray-300 hover:text-yellow-400 transition duration-200"
                        onClick={handleMobileLinkClick}
                      >
                        <div className="flex items-center space-x-2">
                          {/* <span className="text-lg">{item.icon}</span> */}
                          <span>{item.label}</span>
                        </div>
                        {item.desc && (
                          <p className="text-xs text-gray-500 mt-1 ml-7">{item.desc}</p>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link 
                to="/fleet" 
                className={`transition duration-300 py-2 px-2 ${
                  isActive('/fleet') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={handleMobileLinkClick}
              >
                Our Fleet
              </Link>

              {vehicleDropdown.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className={`transition duration-300 py-2 px-2 ${
                    isActive(item.href)
                      ? 'text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                  onClick={handleMobileLinkClick}
                >
                  {item.label}
                </Link>
              ))}

              <Link 
                to="/tour-packages" 
                className={`transition duration-300 py-2 px-2 ${
                  isActive('/tour-packages') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={handleMobileLinkClick}
              >
                Tour Packages
              </Link>
              
              <Link 
                to="/contact" 
                className={`transition duration-300 py-2 px-2 ${
                  isActive('/contact') 
                    ? 'text-yellow-400' 
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
                onClick={handleMobileLinkClick}
              >
                Contact
              </Link>
              
              {/* Mobile Action Buttons */}
              <div className="flex flex-col space-y-3 pt-4 border-t border-gray-800">
                {/* Phone button is now in header, but keeping this for additional context */}
                <Link 
                  to="/signin" 
                  className="flex items-center space-x-2 bg-gray-800 px-4 py-3 rounded-lg hover:bg-gray-700 transition duration-300 cursor-pointer justify-center"
                  onClick={handleMobileLinkClick}
                >
                  <LogIn size={18} />
                  <span className="font-semibold">Sign In / Register</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
